import React, { useState, useEffect, useContext } from 'react';
import { Grid, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@material-ui/core';
import { colors } from '../../../theme/color-palette';
import InventoryTrends from '../InventoryTrends';
import {
	INVENTORY_TRENDS,
	INVENTORY_ITEM_DATA_NOT_AVAILABLE,
	ERROR_SCREEN_MSG_STORE,
	EXCEL_ONHAND_INVENTORY,
	ON_ORDER,
	DC_TO_STORE,
	WAREHOUSE,
	REPL_IN_STOCK,
} from '../../../constants/constant';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import DashboardChart from '../../../common/components/charts/DashboardChart/DashboardChart';
import { DrillDownTrend } from '../Common';
import {
	getItemOnHand,
	getItemOnHandUnits,
	getItemOnOrder,
	getItemOnOrderUnits,
	getItemDCToStore,
	getItemDCToStoreUnits,
	getItemInWhse,
	getItemInWhseUnits,
	getItemInvTrend,
	getItemInStock,
} from '../../../redux/actions/itemDrillDownActions';
import { UserPortalContext } from '../../../context/appcontext';
import debounce from 'lodash/debounce';
import analytics from '../../../helper/analytics';
import tracker from '../../../constants/tracker';
import WeekOfSupplyWrapper from '../../../common/components/charts/WeekOfSupply/WeekOfSupplyWrapper';

const Item = styled(Grid)(({ theme }) => ({
	...theme.typography.body2,
	padding: theme.spacing(1),
	textAlign: 'center',
	color: theme.palette.text.secondary,
}));

const useOnHandStyles = makeStyles(() => ({
	container: {
		width: (P) => P.chartWidth,
		border: `1px solid ${colors.LIGHT_GREY}`,
		borderRadius: '8px',
		height: '430px',
		margin: '8px',
	},
}));

function InventorySummary({
	weekFilter,
	category,
	selectedFilter,
	selectedTab,
	startDate,
	endDate,
	itemNum,
	itemName,
}) {
	const portalUserContext = useContext(UserPortalContext);
	const dispatch = useDispatch();
	const classes = useOnHandStyles();
	const [width, setWidth] = useState(0);

	const { itemOnHandInventory, itemOnHandInventoryUnit } = useSelector(
		(state) => state.itemDrillDownReducer
	);

	const { itemInventoryOnOrder, itemInventoryOnOrderUnit } = useSelector(
		(state) => state.itemDrillDownReducer
	);

	const { itemInventoryDCToStore, itemInventoryDCToStoreUnit } = useSelector(
		(state) => state.itemDrillDownReducer
	);

	const { itemInventoryInWhse, itemInventoryInWhseUnit } = useSelector(
		(state) => state.itemDrillDownReducer
	);

	const itemInventoryTrend = useSelector(
		(state) => state.itemDrillDownReducer.itemTrendData
	);
	const itemInventoryTrendErrorCode = useSelector(
		(state) => state.itemDrillDownReducer.itemTrendErrorCode
	);

	const itemInvTrendIsLoading = useSelector(
		(state) => state.itemDrillDownReducer.itemTrendLoading
	);

	const itemInStock = useSelector(
		(state) => state.itemDrillDownReducer.itemInStockData
	);

	const itemInStockLoading = useSelector(
		(state) => state.itemDrillDownReducer.itemInStockDataLoading
	);

	const itemInStockErrorCode = useSelector(
		(state) => state.itemDrillDownReducer.itemInStockDataErrorCode
	);

	const kpiName = useSelector((state) => state.salesReducer.kpiName);

	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);

	const onHandData = {
		retail: itemOnHandInventory,
		unit: itemOnHandInventoryUnit,
	};

	const onOrderData = {
		retail: itemInventoryOnOrder,
		unit: itemInventoryOnOrderUnit,
	};

	const dcToStoreData = {
		retail: itemInventoryDCToStore,
		unit: itemInventoryDCToStoreUnit,
	};

	const inWhseData = {
		retail: itemInventoryInWhse,
		unit: itemInventoryInWhseUnit,
	};

	useEffect(() => {
		const params = {
			categoryId: '-1',
			period: weekFilter,
			portalUserContext,
			itemNum,
			selectedFilter,
			startDate,
			endDate,
		};
		dispatch(getItemOnHand(params));
		dispatch(getItemOnHandUnits(params));
		dispatch(getItemOnOrder(params));
		dispatch(getItemOnOrderUnits(params));
		dispatch(getItemDCToStore(params));
		dispatch(getItemDCToStoreUnits(params));
		dispatch(getItemInWhse(params));
		dispatch(getItemInWhseUnits(params));
		dispatch(getItemInvTrend(params));
		dispatch(getItemInStock(params));
	}, [weekFilter, category, itemNum, startDate, endDate, selectedView]);

	useEffect(() => {
		const updateSize = debounce(() => {
			setWidth(window.innerHeight + window.innerWidth);
		}, 1500);
		window.addEventListener('resize', updateSize);
		updateSize();
		const pageid = `${kpiName}Item${selectedTab}`
			.replaceAll(/\s/g, '')
			.toUpperCase();
		analytics.trackPageInfo(tracker.PAGE_ID[pageid]);
		return () => window.removeEventListener('resize', updateSize);
	}, []);

	return (
		<Box sx={{ flexGrow: 1 }}>
			<Grid container>
				<Grid
					data-test-id="on-hand-inventory"
					item
					xs={12}
					sm={6}
					md={6}
					lg={4}>
					<Item className={classes.container}>
						<DrillDownTrend
							chartName={EXCEL_ONHAND_INVENTORY}
							data={{ ...onHandData }}
							loading={itemOnHandInventory?.loading || false}
							category={category}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
						/>
					</Item>
				</Grid>
				<Grid data-test-id="inventory-trends" item xs={12} sm={6} md={6} lg={4}>
					<Item>
						<InventoryTrends
							chartData={itemInventoryTrend}
							chartTitle={INVENTORY_TRENDS}
							chartName="InventoryTrend"
							weekFilter={weekFilter}
							category={category}
							chartHeight="340px"
							isLoading={itemInvTrendIsLoading}
							selectedTab={selectedTab}
							errorMsg={
								itemInventoryTrendErrorCode === 204
									? INVENTORY_ITEM_DATA_NOT_AVAILABLE
									: ERROR_SCREEN_MSG_STORE
							}
							showWeekDates={true}
							showTitleInErrorContent={false}
						/>
					</Item>
				</Grid>
				<Grid data-test-id="in-stock-chart" item xs={12} sm={6} md={6} lg={4}>
					<Item>
						<DashboardChart
							chartData={itemInStock}
							chartTitle={`${REPL_IN_STOCK}`}
							chartName={'in-stock'}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
							isLoading={itemInStockLoading}
							errorMsg={
								itemInStockErrorCode === 204
									? INVENTORY_ITEM_DATA_NOT_AVAILABLE
									: ERROR_SCREEN_MSG_STORE
							}
							category={category}
							selectedFilter={selectedFilter}
							showWeekDates={true}
							updatedWidth={width}
							showTitleInErrorContent={false}
							showExploreButton={false}
						/>
					</Item>
				</Grid>
				<Grid data-test-id="on-order-chart" item xs={12} sm={6} md={6} lg={4}>
					<Item className={classes.container}>
						<DrillDownTrend
							chartName={ON_ORDER}
							data={{ ...onOrderData }}
							loading={itemInventoryOnOrder?.loading || false}
							category={category}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
						/>
					</Item>
				</Grid>
				<Grid
					data-test-id="dc-to-store-chart"
					item
					xs={12}
					sm={6}
					md={6}
					lg={4}>
					<Item className={classes.container}>
						<DrillDownTrend
							chartName={DC_TO_STORE}
							data={{ ...dcToStoreData }}
							loading={itemInventoryDCToStore?.loading || false}
							category={category}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
						/>
					</Item>
				</Grid>
				<Grid item xs={12} sm={6} md={6} lg={4} data-test-id="in-whse">
					<Item className={classes.container}>
						<DrillDownTrend
							chartName={WAREHOUSE}
							data={{ ...inWhseData }}
							loading={itemInventoryInWhse?.loading || false}
							category={category}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
						/>
					</Item>
				</Grid>
				<Grid
					data-test-id="weeks-of-supply-store"
					item
					xs={12}
					sm={6}
					md={6}
					lg={6}
					xl={6}>
					<Item className={classes.container}>
						<WeekOfSupplyWrapper
							wosType={'Store'}
							selectedTab={selectedTab}
							weekFilter={weekFilter}
							startDate={startDate}
							endDate={endDate}
							itemName={itemName}
							itemNum={itemNum}
						/>
					</Item>
				</Grid>
				{/* Week of supply - DC */}
				<Grid
					data-test-id="weeks-of-supply-dc"
					item
					xs={12}
					sm={6}
					md={6}
					lg={6}
					xl={6}>
					<Item className={classes.container}>
						<WeekOfSupplyWrapper
							wosType={'DC'}
							selectedTab={selectedTab}
							weekFilter={weekFilter}
							startDate={startDate}
							endDate={endDate}
							itemName={itemName}
							itemNum={itemNum}
						/>
					</Item>
				</Grid>
			</Grid>
		</Box>
	);
}

InventorySummary.propTypes = {
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	selectedFilter: PropTypes.string,
	selectedTab: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	itemNum: PropTypes.string,
	itemName: PropTypes.string,
};

export default InventorySummary;
